// Pure formatting for the staff Today briefing (staff-food-at-house). Kept
// free of React and fetch so it unit-tests in isolation, mirroring
// shiftLine.ts. All dates are ISO YYYY-MM-DD strings from the API — never
// JS-Date round-trips (the ±1-day serialization trap).

import { parseLocal } from '../../lib/dates';
import type { SelfViewWeek } from '@bbbk/schema';

// The short date idiom — "Sat 19 Jul". THE one composition of short weekday +
// day + short month: the leaves pills and the day-after pill label both read
// it from here, so the format cannot drift between them.
export function pillDate(dateIso: string): string {
  const d = parseLocal(dateIso);
  const wd = d.toLocaleDateString('en-IE', { weekday: 'short' });
  const mon = d.toLocaleDateString('en-IE', { month: 'short' });
  return `${wd} ${d.getDate()} ${mon}`;
}

// "leaves today" (checkout is the server-decided today) or "leaves Sat 19 Jul".
// The pill answers ONE question: is a food run / med plan needed before the
// dog goes home?
export function leavesLabel(leavesOn: string, todayIso: string): string {
  return leavesOn === todayIso ? 'leaves today' : `leaves ${pillDate(leavesOn)}`;
}

// The long weekday alone ("Saturday") — the look-ahead day's hero eyebrow.
export function weekdayLong(dateIso: string): string {
  return parseLocal(dateIso).toLocaleDateString('en-IE', { weekday: 'long' });
}

// The long hero date: "Thursday 16 July". Composed from parts — a combined
// toLocaleDateString inserts a comma after the weekday, which the signed-off
// prototype does not carry.
export function heroDate(todayIso: string): string {
  const d = parseLocal(todayIso);
  const mon = d.toLocaleDateString('en-IE', { month: 'long' });
  return `${weekdayLong(todayIso)} ${d.getDate()} ${mon}`;
}

// The next upcoming shift strictly after today, from the already-fetched
// self-view weeks — "Sat 19 July · AM" (or AM + PM), or null when the rota
// holds nothing ahead. Powers the not-working hero line without any extra
// endpoint or data beyond the viewer's own shifts.
export function nextShiftLabel(weeks: SelfViewWeek[], todayIso: string): string | null {
  const byDate = new Map<string, Set<string>>();
  for (const w of weeks) {
    for (const s of w.shifts) {
      if (s.date <= todayIso) continue;
      const kinds = byDate.get(s.date) ?? new Set<string>();
      kinds.add(s.kind);
      byDate.set(s.date, kinds);
    }
  }
  const first = [...byDate.keys()].sort()[0];
  if (!first) return null;
  const d = parseLocal(first);
  const wd = d.toLocaleDateString('en-IE', { weekday: 'short' });
  const mon = d.toLocaleDateString('en-IE', { month: 'long' });
  const label = `${wd} ${d.getDate()} ${mon}`;
  const kinds = byDate.get(first)!;
  const slot = kinds.has('am') && kinds.has('pm') ? 'AM + PM' : kinds.has('am') ? 'AM' : 'PM';
  return `${label} · ${slot}`;
}

// The Shifts tab renders from the current week onward (Freddi's cut — no
// previous weeks). Pure slice, unit-testable.
export function shiftsPanelWeeks(weeks: SelfViewWeek[]): SelfViewWeek[] {
  const i = weeks.findIndex((w) => w.current);
  return i <= 0 ? weeks : weeks.slice(i);
}

// Month divider before the first rendered week that STARTS a new month
// relative to its predecessor — "August". A week spanning the boundary
// (27 July – 2 August) stays with the month it starts in.
export function monthDividerBefore(prev: SelfViewWeek | undefined, week: SelfViewWeek): string | null {
  if (!prev) return null;
  if (prev.from.slice(0, 7) === week.from.slice(0, 7)) return null;
  return parseLocal(week.from).toLocaleDateString('en-IE', { month: 'long' });
}

// The Dublin calendar date for a timestamp — the same "today" the server
// decides with. The staff view compares this against the briefing's date to
// notice the day boundary passing while the page stays open. en-CA formats
// as YYYY-MM-DD, matching the wire format exactly. The formatter is built
// ONCE at module scope — Intl.DateTimeFormat construction is expensive and
// this runs on the render path (pill label fallback) and every staleness tick.
const DUBLIN_DATE_FMT = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Dublin' });
export function dublinDateOf(t: number): string {
  return DUBLIN_DATE_FMT.format(new Date(t));
}
